import { FirestoreDateLike, toDateSafe } from '@utils/Date';

/**
 * El PRIMER renglón de la bitácora de un objeto, **derivado de lo que el objeto
 * ya sabe de sí mismo** — no de un documento en `activity-logs` (D8 del
 * `PLAN-BITACORA-ACTIVITY-LOGS.md`, idea de Irving).
 *
 * Por qué: un módulo que nunca tuvo bitácora abre el drawer vacío, y vacío se
 * lee como *"aquí nunca pasó nada"* en vez de *"empezamos a registrar apenas"*.
 * Si el documento trae `createdAt`/`createdBy`, eso YA es el rastro de su
 * creación: escribir un log para repetirlo sería duplicar (D6) y costaría una
 * escritura por objeto — trivial en 90 productos, absurdo en 64,500 pedidos.
 *
 * **Cuesta cero.** Ni una lectura ni una escritura: se arma con el objeto que la
 * pantalla ya tiene en memoria.
 *
 * ⚠️ **Este módulo es PURO: no importa Firebase ni servicios.** El escritor del
 * sello vive aparte (`CreationStamp.ts`) justo por eso — al meterlo aquí, la
 * cadena `UserServ → Firebase → redux-persist` tumbó cuatro suites de pruebas de
 * golpe, incluidas las del snapshot del catálogo, que solo venían a importar
 * `stripAuditFields`.
 *
 * ⚠️ **Medido contra producción (2026-09-10), y de aquí salen sus dos reglas:**
 * de las colecciones con rastro, **solo dos traen autor** (`inventory-orders` al
 * 27 %, `cash-flows`), y `customers`, `users` y `tables_kiosks` traen la fecha
 * sola. Por eso: ① el renglón tiene que **verse bien sin nombre**, y ② si no hay
 * fecha **no se inventa nada** — no hay renglón y el drawer lo dice.
 */
export interface ActivityOrigin {
    /**
     * Milisegundos, **no un `Date`**: este objeto viaja en Redux y el slice `app`
     * está persistido con redux-persist. Un `Date` se guarda en localStorage como
     * string y **vuelve como string** al rehidratar — un `.toDate()` o un
     * `format()` río abajo reventaría solo después de recargar la página, que es
     * la peor forma de fallar.
     */
    dateMillis: number;
    /** La frase ya armada, lista para pintar. */
    text: string;
}

/**
 * Lo MÍNIMO que un objeto tiene que saber de sí mismo para dar su renglón. Cada
 * campo es opcional porque cada colección los nombra distinto (y el catálogo no
 * trae ninguno): `createdAt` en clientes, usuarios, mesas y el catálogo nuevo;
 * `date` en cajas y movimientos de caja.
 */
export interface ActivityOriginSource {
    createdAt?: FirestoreDateLike;
    date?: FirestoreDateLike;
    createdBy?: unknown;
    username?: string;
}

// Lo que llega es un Customer, una CashRegister o un InventoryOrder ENTEROS: el
// resto de sus campos no le importa a esta función, pero tiene que caber. Por eso
// la unión con Record en vez de un índice de cadena en la interfaz — con índice,
// los tipos del proyecto (`Order`, `Dish`…), que no lo declaran, no encajarían.
export type ActivityOriginInput = ActivityOriginSource | Record<string, unknown> | undefined | null;

export interface ActivityOriginOptions {
    /** Cómo se lee CON autor: `'creó el pedido'` → *"Joan Guemes creó el pedido"*. */
    verb?: string;
    /** Cómo se lee SIN autor: `'Pedido creado'`. En participio, porque no hay quién. */
    noun?: string;
    /** Coletilla opcional: `'desde Av. Juárez'`. */
    detail?: string;
    /** El autor, cuando la pantalla ya lo resolvió por su cuenta (p. ej. desde un `userId`). */
    name?: string;
}

/**
 * El nombre de quien creó el documento, tolerando las tres formas que hay hoy:
 * `createdBy` como `UserType` completo (`inventory-orders`), como `{ uid, name }`
 * (lo que se siembra de aquí en adelante, D9) y `username` suelto
 * (`cash-registers`).
 *
 * ⚠️ **Un uid NO es un nombre.** `createdBy` guardado como string es un id: se
 * descarta. *"aObMEojWiUdQITZmZ4WNw3Y7z8h1 creó el pedido"* no le dice nada a
 * nadie; *"Pedido creado"* sí.
 */
export const getOriginAuthorName = (source: ActivityOriginInput, fallback?: string): string | undefined => {
    const createdBy = (source as ActivityOriginSource)?.createdBy as { profile?: { name?: string }; name?: string } | string | undefined;
    const porObjeto = typeof createdBy === 'object' ? createdBy : undefined;
    const candidatos = [porObjeto?.profile?.name, porObjeto?.name, (source as ActivityOriginSource)?.username, fallback];
    const nombre = candidatos.find((valor) => typeof valor === 'string' && valor.trim() !== '');
    return nombre ? String(nombre).trim() : undefined;
};

/**
 * Arma el renglón, o `undefined` si el objeto no sabe cuándo nació — que es el
 * caso de TODO el catálogo hoy (dishes, recipes, modifier-groups, inputs,
 * input-categories, product-categories: cero campos de creación, medido).
 */
export const buildActivityOrigin = (
    source: ActivityOriginInput,
    options: ActivityOriginOptions = {}
): ActivityOrigin | undefined => {
    if (!source) return undefined;
    const doc = source as ActivityOriginSource;
    // `createdAt` es el campo del catálogo nuevo y de clientes/usuarios/mesas;
    // `date` es como lo llaman las cajas y los movimientos de caja.
    const date = toDateSafe(doc.createdAt) || toDateSafe(doc.date);
    if (!date) return undefined;

    const name = getOriginAuthorName(source, options.name);
    const base = name ? `${name} ${options.verb || 'creó este registro'}` : options.noun || 'Creado';
    const detail = options.detail?.trim();
    return { dateMillis: date.getTime(), text: detail ? `${base} ${detail}` : base };
};

/** Los campos de auditoría, para quitarlos de donde no pintan nada. */
export const AUDIT_FIELDS = ['createdAt', 'createdBy'] as const;

/**
 * Quita los campos de auditoría de un documento.
 *
 * ⚠️ **Existe por el `catalog/snapshot`**, que vive en UN documento con límite de
 * 1 MiB y es el que hace que el catálogo cueste una lectura en vez de 336. Quién
 * creó un producto **no le sirve al POS, ni a la web, ni al server**; metérselo
 * serían decenas de KB de auditoría en el documento que se optimizó justo para
 * que fuera pequeño, más datos del staff en algo que lee cualquier visitante.
 */
export const stripAuditFields = <T extends object>(document: T): T => {
    const copia = { ...(document as Record<string, unknown>) };
    AUDIT_FIELDS.forEach((field) => delete copia[field]);
    return copia as T;
};
